import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import {
  Building,
  Check,
  X,
  Crown,
  User,
  Loader2,
} from "lucide-react";
import { toast } from "../../hooks/use-toast";
import {
  getUserPendingOrganizationInvitations,
  acceptOrganizationInvitation,
  rejectOrganizationInvitation,
} from "../../services/organizationService";
import { OrganizationInvitation } from "../../types/organization";

interface OfflineFirstInvitationNotificationsProps {
  onInvitationHandled?: () => void;
}

export function OfflineFirstInvitationNotifications({
  onInvitationHandled,
}: OfflineFirstInvitationNotificationsProps) {
  const [orgInvitations, setOrgInvitations] = useState<OrganizationInvitation[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [selectedInvitation, setSelectedInvitation] = useState<{
    type: "organization";
    invitation: OrganizationInvitation;
    action: "accept" | "reject";
  } | null>(null);

  const loadInvitations = async () => {
    try {
      setLoading(true);
      const orgData = await getUserPendingOrganizationInvitations();
      setOrgInvitations((orgData || []).filter(inv => inv && inv.id));
    } catch (error) {
      console.error('[InvitationNotifications] Error loading invitations:', error);
      toast({
        title: "Error",
        description: "Failed to load invitations",
        variant: "destructive",
      });
      setOrgInvitations([]);
      setTeamInvitations([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInvitations();
  }, []);

  const handleAction = async () => {
    if (!selectedInvitation?.invitation) return;

    try {
      setActionLoading(selectedInvitation.invitation.id);

      if (selectedInvitation.action === "accept") {
        await acceptOrganizationInvitation(selectedInvitation.invitation.id);
      } else {
        await rejectOrganizationInvitation(selectedInvitation.invitation.id);
      }

      toast({
        title: "Success",
        description: `Invitation ${selectedInvitation.action}ed successfully`,
      });

      loadInvitations();
      onInvitationHandled?.();
    } catch (error) {
      console.error(`[InvitationNotifications] Error ${selectedInvitation.action}ing invitation:`, error);
      toast({
        title: "Error",
        description: `Failed to ${selectedInvitation.action} invitation`,
        variant: "destructive",
      });
    } finally {
      setActionLoading(null);
      setSelectedInvitation(null);
    }
  };

  const totalInvitations = orgInvitations.length;

  if (totalInvitations === 0) {
    return null;
  }

  return (
    <>
      {/* Confirmation Dialog */}
      <AlertDialog
        open={!!selectedInvitation}
        onOpenChange={() => setSelectedInvitation(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center space-x-2">
              {selectedInvitation?.action === "accept" ? (
                <Check className="w-5 h-5 text-green-600" />
              ) : (
                <X className="w-5 h-5 text-red-600" />
              )}
              <span>
                {selectedInvitation?.action === "accept" ? "Accept" : "Reject"}{" "}
                Invitation
              </span>
            </AlertDialogTitle>
            <AlertDialogDescription>
              {selectedInvitation?.action === "accept" ? (
                <>
                  Are you sure you want to accept this invitation to join{" "}
                  <strong>{selectedInvitation?.invitation?.organization_name}</strong>?
                </>
              ) : (
                <>
                  Are you sure you want to reject this invitation to join{" "}
                  <strong>{selectedInvitation?.invitation?.organization_name}</strong>
                  ?
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleAction}
              disabled={!!actionLoading}
              className={
                selectedInvitation?.action === "accept"
                  ? "bg-green-600 hover:bg-green-700"
                  : "bg-red-600 hover:bg-red-700"
              }
            >
              {actionLoading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-1" />
              ) : null}
              {selectedInvitation?.action === "accept" ? "Accept" : "Reject"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-4"
      >
        <Card className="border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/20">
          <CardContent className="space-y-4 pt-4">
            {/* Organization Invitations */}
            <AnimatePresence>
              {orgInvitations.map((invitation) => (
                <motion.div
                  key={invitation.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  className="flex items-center justify-between p-4 bg-white dark:bg-gray-900 border rounded-lg"
                >
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                      <Building className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <p className="font-medium">
                        Organisation:{" "}
                        {invitation.organization_name || "Unknown Organisation"}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Invited as{" "}
                        <Badge
                          variant={invitation.role === "admin" ? "default" : "secondary"}
                        >
                          {invitation.role === "admin" ? (
                            <><Crown className="w-3 h-3 mr-1" />Admin</>
                          ) : (
                            <><User className="w-3 h-3 mr-1" />Member</>
                          )}
                        </Badge>
                      </p>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-green-600 border-green-200 hover:bg-green-50"
                      disabled={!!actionLoading}
                      onClick={() =>
                        setSelectedInvitation({
                          type: "organization",
                          invitation,
                          action: "accept",
                        })
                      }
                    >
                      <Check className="w-4 h-4 mr-1" />
                      Accept
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 border-red-200 hover:bg-red-50"
                      disabled={!!actionLoading}
                      onClick={() =>
                        setSelectedInvitation({
                          type: "organization",
                          invitation,
                          action: "reject",
                        })
                      }
                    >
                      <X className="w-4 h-4 mr-1" />
                      Reject
                    </Button>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>

          </CardContent>
        </Card>
      </motion.div>
    </>
  );
}
